import { Injectable, Inject } from '@angular/core';
import { DOCUMENT } from '@angular/common';

@Injectable({
  providedIn: 'root'
})
export class PrintService {
  private windowRef: Window | null = null;

  // Inject DOCUMENT for server-side rendering safety and to get the window reference
  constructor(@Inject(DOCUMENT) private document: Document) { 
    this.windowRef = this.document.defaultView;
  }

  /**
   * Generates and prints the content in a new temporary window.
   * @param htmlContent The raw HTML string to be printed (e.g., a receipt).
   * @param styleCss An optional CSS string to include for styling the print-out.
   */
  public printHtmlDocument(htmlContent: string, styleCss?: string, printerType?: boolean): void {
    // 1. Create a hidden iframe element
    const iframe = this.document.createElement('iframe');
    iframe.setAttribute('style', 'visibility: hidden; position: fixed; right: 0; bottom: 0; height: 0; width: 0;');
    
    // 2. Append it to the document body
    this.document.body.appendChild(iframe);

    // Get the iframe's document object
    const iframeDoc = iframe.contentWindow?.document;

    if (!iframeDoc) {
      console.error('Could not access iframe document for printing.');
      this.document.body.removeChild(iframe);
      return;
    }

    /**
     * Thermal printer
     */
    const thermalStyles = `
      @page {
        margin: 0;
        size: auto;
      }

      body {
        margin: 0;
        max-width: 80mm;
        font-family: monospace;
        font-size: 12px;
      }

      .ticket {
        width: 100%;
      }
    `;

    /**
     * Normal printer
     * A4 landscape with multiple tickets per page.
     */
    const normalPrinterStyles = `
      @page {
        size: A4 landscape;
        margin: 10mm;
      }

      body {
        margin: 0;
        padding: 0;
      }

      .tickets-container {
        display: grid;
        grid-template-columns: 1fr 1fr;
        column-gap: 8mm;
        align-items: start;
        width: 100%;
      }

      .ticket {
        display: block;
        width: 100%;
        padding: 5mm;
        margin: 0;
        break-inside: avoid;
        page-break-inside: avoid;
      }
    `;

    const printStyles = printerType ? thermalStyles : normalPrinterStyles;

    // 3. Build the full HTML document content for the iframe
    const documentHtml = `
      <html>
        <head>
          <title>Print Document</title>
          <style>
            ${printStyles}
            ${styleCss || ''}
          </style>
        </head>
        <body>
          ${printerType ? htmlContent : `
              <div class="tickets-container">
                ${htmlContent}
              </div>
            `
          }
        </body>
      </html>
    `;

    // 4. Write content to the iframe document
    iframeDoc.open();
    iframeDoc.write(documentHtml);
    iframeDoc.close();

    // Wait for content (especially images) to load, then print and clean up
    iframe.onload = () => {
      iframe.contentWindow?.focus(); // Ensure the iframe is focused
      iframe.contentWindow?.print(); // Open the print dialog

      // Clean up the iframe after a short delay
      setTimeout(() => {
        if (this.document.body.contains(iframe)) {
          this.document.body.removeChild(iframe);
        }
      }, 500);
    };

    // Fallback for browsers that don't trigger iframe.onload (less common)
    if (!iframe.contentWindow?.print) {
      iframe.contentWindow?.focus();
      iframe.contentWindow?.print();
      setTimeout(() => {
        this.document.body.removeChild(iframe);
      }, 500); 
    }
  }
}
